import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, Typography, Button } from '@mui/material';
import { SnackbarProvider } from 'notistack';


import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HeartbeatProvider } from './contexts/HeartbeatContext';

// 404 Components
import Smart404Router from './components/Smart404Router';
import Static404 from './components/Static404';
import CompanyLoginWrapper from './components/CompanyLoginWrapper';
import CompanyPasswordResetWrapper from './components/CompanyPasswordResetWrapper';
import CompanySignupWrapper from './components/CompanySignupWrapper';


// Layout components
import AdminLayout from './layouts/AdminLayout';
import ClientLayout from './layouts/ClientLayout';
import ClientLayoutNew from './layouts/ClientLayoutNew';

// Auth pages
import AdminLogin from './pages/auth/AdminLogin';
import ClientLogin from './pages/auth/ClientLogin';

// Admin pages
import ClientManagement from './pages/admin/ClientManagement';
import FeaturesManagement from './pages/admin/FeaturesManagement';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminDashboardContent from './pages/admin/AdminDashboardContent';
import AdminManagement from './pages/admin/AdminManagement';
import UploadData from './pages/admin/UploadData';
import UploadHistory from './pages/admin/UploadHistory';
import SupportInquiries from './pages/admin/SupportInquiries';
import WHMPage from './pages/admin/WHMPage';
import EmailHub from './pages/admin/EmailHub';
import AddClientPage from './pages/admin/AddClientPage';
import AddAdminPage from './pages/admin/AddAdminPage';
import ServerStatusPage from './pages/admin/ServerStatusPage';
import DataMaintenance from './pages/admin/DataMaintenance';

// Client pages - Role-based dashboards
import DashboardViewer from './pages/client/DashboardViewer';
import DashboardEditor from './pages/client/DashboardEditor';
import DashboardAdmin from './pages/client/DashboardAdmin';
import SuperAdminViewer from './pages/client/SuperAdminViewer';
import UserStatus from './pages/client/UserStatus';
import AssignedTasks from './pages/client/AssignedTasks';
import CRM from './pages/client/CRM';
import CRMTemplates from './pages/client/CRMTemplates';
import CRMMailbox from './pages/client/CRMMailbox';
import CRMAutomations from './pages/client/CRMAutomations';

// Special pages
import AccountDeactivated from './pages/AccountDeactivated';
import PasswordResetComplete from './pages/PasswordResetComplete';
import ThankYou from './pages/ThankYou';
import AdminPasswordReset from './pages/AdminPasswordReset';
import ClientPasswordReset from './pages/ClientPasswordReset';
import AdminSignup from './pages/AdminSignup';
import ClientSignup from './pages/ClientSignup';

// Root Redirect component that checks for force redirect first
const RootRedirect: React.FC = () => {
  // Check if we have a saved login URL for this company
  const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
  if (savedLoginUrl) {
    // Extract company slug from saved URL and redirect directly to admin login
    const savedPathParts = savedLoginUrl.split('/');
    const savedCompanySlug = savedPathParts[1];
    if (savedCompanySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(savedCompanySlug)) {
      return <Navigate to="/admin/login" replace />;
    }
  }
  
  // Default to admin login
  return <Navigate to="/admin/login" replace />;
};

// Admin Dashboard Redirect component
const AdminDashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (!user?.username) {
    return <Navigate to="/admin/login" />;
  }
  
  return <Navigate to={`/admin/${user.username}/dashboard`} replace />;
};

// Role-based Dashboard component
const RoleBasedDashboard: React.FC = () => {
  const { user } = useAuth();
  const location = window.location.pathname;

  // CRITICAL: Check if we're in the middle of a logout process
  // If we're on a company-specific route and there's no user, don't redirect immediately
  // This prevents the component from redirecting during logout
  const pathParts = location.split('/');
  const companySlug = pathParts[1];
  const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
  
  if (!user?.username) {
    // If we're on a company-specific route, redirect to company-specific login
    if (isCompanySpecific) {
      return <Navigate to={`/${companySlug}/login`} />;
    }
    
    // For generic client routes, redirect to signup for security
    if (location.includes('/client/')) {
      return <Navigate to="/client-signup" />;
    }
    
    // Only redirect to signup if we're not in a company-specific context
    return <Navigate to="/client-signup" />;
  }

  // Use the already declared variables from above

  if (!isCompanySpecific) {
    // If not in company-specific context, redirect to signup
    return <Navigate to="/client-signup" replace />;
  }

  if (user.role === 'editor') {
    return <Navigate to={`/${companySlug}/${user.username}/dashboard/sales`} replace />;
  } else if (user.role === 'viewer') {
    // Regular viewers access the viewer dashboard
    return <Navigate to={`/${companySlug}/${user.username}/dashboard/viewer`} replace />;
  } else if (user.role === 'super_admin_viewer') {
    // Super admin viewers get their own dedicated dashboard
    return <Navigate to={`/${companySlug}/${user.username}/super-admin-viewer`} replace />;
  } else if (user.role === 'admin') {
    return <Navigate to={`/${companySlug}/${user.username}/dashboard/admin`} replace />;
  }

  // Default to viewer dashboard
  return <Navigate to={`/${companySlug}/${user.username}/dashboard/viewer`} replace />;
};


// Protected Route component
const ProtectedRoute: React.FC<{
  element: React.ReactElement;
  requiredRole?: 'admin' | 'client';
}> = ({ element, requiredRole }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const isTemporaryAccess = window.location.search.includes('temp_access=true');
  const tempToken = sessionStorage.getItem('temp_client_token');
  const location = window.location.pathname;

  if (loading) {
    return <div>Loading...</div>;
  }

  // SECURITY: Validate route parameters to prevent invalid URLs from matching
  const pathParts = location.split('/');
  const companySlug = pathParts[1];
  const username = pathParts[2];

  // For admin routes like /admin/:username, validate the username parameter
  if (requiredRole === 'admin' && companySlug === 'admin' && username) {
    // If the username looks like it should be a different route (login, signup, etc.), show 404
    const invalidAdminPaths = ['login', 'logi', 'log', 'signup', 'signu', 'sign', 'password-reset', 'reset', 'dashboard', 'clients', 'admins'];
    if (invalidAdminPaths.some(invalid => username.toLowerCase().startsWith(invalid.substring(0, 3)) || invalid.startsWith(username.toLowerCase()))) {
      // Return Smart404Router to use proper redirect logic
      return <Smart404Router />;
    }
  }

  // For company routes like /:companySlug/:username, validate both parameters
  if (requiredRole === 'client' && companySlug && username && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
    // If the username looks like it should be a different route, show 404
    const invalidClientPaths = ['login', 'logi', 'log', 'signup', 'signu', 'sign', 'password-reset', 'reset', 'dashboard', 'client-signup'];
    if (invalidClientPaths.some(invalid => username.toLowerCase().startsWith(invalid.substring(0, 3)) || invalid.startsWith(username.toLowerCase()))) {
      // Return Smart404Router to use proper redirect logic
      return <Smart404Router />;
    }
  }

  // Handle temporary client access
  if (requiredRole === 'client' && isTemporaryAccess && tempToken) {
    return element;
  }

  // Regular authentication check
  if (!isAuthenticated) {
    // Check if we're in a company-specific context
    const pathParts = location.split('/');
    const companySlug = pathParts[1];
    
            if (requiredRole === 'client' && companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
      // Redirect to company-specific login
      return <Navigate to={`/${companySlug}/login`} />;
    }
    
    // For generic client routes, redirect to signup for security
    if (requiredRole === 'client' && location.includes('/client/')) {
      return <Navigate to="/client-signup" />;
    }
    
    return <Navigate to={requiredRole === 'admin' ? '/admin/login' : '/client-signup'} />;
  }

  // Allow all valid client roles for client routes (including super admin viewer)
  if (requiredRole === 'client' && !['client', 'editor', 'viewer', 'admin', 'super_admin_viewer'].includes(user?.role || '')) {
    // Check if we're in a company-specific context
    const pathParts = location.split('/');
    const companySlug = pathParts[1];
    
    if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
      return <Navigate to={`/${companySlug}/login`} />;
    }
    
    // For generic client routes, redirect to signup for security
    if (location.includes('/client/')) {
      return <Navigate to="/client-signup" />;
    }
    
    return <Navigate to="/client-signup" />;
  }

  // Role check for admin
  if (requiredRole === 'admin' && user?.role !== 'admin') {
    return <Navigate to={`/${user?.role}/login`} />;
  }

  return element;
};

// Client Admin Protected Route component
const ClientAdminProtectedRoute: React.FC<{
  element: React.ReactElement;
}> = ({ element }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = window.location.pathname;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Check if we're in a company-specific context
    const pathParts = location.split('/');
    const companySlug = pathParts[1];
    
    if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
      return <Navigate to={`/${companySlug}/login`} />;
    }
    
    // For generic client routes, redirect to signup for security
    if (location.includes('/client/')) {
      return <Navigate to="/client-signup" />;
    }
    
    return <Navigate to="/client-signup" />;
  }

      // Check if user is a client admin
    if (user?.role !== 'admin') {
      // Check if we're in a company-specific context
      const pathParts = location.split('/');
      const companySlug = pathParts[1];
      
      if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
        return <Navigate to={`/${companySlug}/${user?.username}/dashboard`} />;
      }
    
    return <Navigate to={`/client/${user?.username}/dashboard`} />;
  }

  return element;
};

const App: React.FC = () => {
  return (
    <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ThemeProvider>
          <AuthProvider>
            <HeartbeatProvider>
              <SnackbarProvider maxSnack={3}>
        
                <CssBaseline />
                <Routes>
                {/* Auth Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                
                {/* Company-Specific Client Login Routes */}
                <Route path="/:companySlug/login" element={<CompanyLoginWrapper />} />
                
                {/* Block old admin-login routes - return 404 */}
                <Route path="/admin-login/login" element={<Static404 />} />
                <Route path="/admin-login/*" element={<Static404 />} />
                
                {/* Company-Specific Auth Routes - Primary client access */}
                <Route path="/:companySlug/password-reset" element={<CompanyPasswordResetWrapper />} />
                <Route path="/:companySlug/client-signup" element={<CompanySignupWrapper />} />
                
                {/* Special Routes */}
                <Route path="/account-deactivated" element={<AccountDeactivated />} />
                <Route path="/admin/password-reset" element={<AdminPasswordReset />} />
                <Route path="/client/password-reset" element={<ClientPasswordReset />} />
                <Route path="/password-reset" element={<ClientPasswordReset />} />
                <Route path="/reset-password" element={<PasswordResetComplete />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/admin-signup" element={<AdminSignup />} />
                <Route path="/client-signup" element={<ClientSignup />} />

                {/* Admin Routes - Legacy redirect */}
                <Route
                  path="/admin"
                  element={<ProtectedRoute element={<AdminDashboardRedirect />} requiredRole="admin" />}
                />

                {/* Admin Routes - User-specific */}
                <Route
                  path="/admin/:username"
                  element={<ProtectedRoute element={<AdminDashboard />} requiredRole="admin" />}
                >
                  {/* Index route for /admin/:username */}
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboardContent />} />
                  <Route path="clients" element={<ClientManagement />} />
                  <Route path="admins" element={<AdminManagement />} />
                  <Route path="features" element={<FeaturesManagement />} />
                  <Route path="upload" element={<UploadData />} />
                  <Route path="history" element={<UploadHistory />} />
                  <Route path="support" element={<SupportInquiries />} />
                  <Route path="whm" element={<WHMPage />} />
                  <Route path="email-hub" element={<EmailHub />} />
                  <Route path="data-maintenance" element={<DataMaintenance />} />
                  <Route path="add-client" element={<AddClientPage />} />
                  <Route path="add-admin" element={<AddAdminPage />} />
                  <Route path="server-status" element={<ServerStatusPage />} />
                </Route>

                {/* Company-Specific Client Routes - Primary client access */}
                <Route
                  path="/:companySlug/:username"
                  element={<ProtectedRoute element={<ClientLayoutNew />} requiredRole="client" />}
                >
                  {/* Index route for /:companySlug/:username */}
                  <Route index element={<RoleBasedDashboard />} />
                  {/* Dashboard routes */}
                  <Route path="dashboard" element={<RoleBasedDashboard />} />
                  <Route path="dashboard/sales" element={<DashboardEditor />} />
                  <Route path="dashboard/viewer" element={<DashboardViewer />} />
                  <Route path="dashboard/admin" element={<DashboardAdmin />} />
                  <Route path="dashboard/super-admin-viewer" element={<SuperAdminViewer />} />
                  {/* Client Admin specific routes */}
                  <Route path="user-status" element={
                    <ClientAdminProtectedRoute element={<UserStatus />} />
                  } />
                  <Route path="assigned-tasks" element={<AssignedTasks />} />
                  <Route path="crm" element={<CRM />} />
                  <Route path="crm/templates" element={<CRMTemplates />} />
                  <Route path="crm/mailbox" element={<CRMMailbox />} />
                  <Route path="crm/automations" element={<CRMAutomations />} />
                  <Route path="mailforge" element={<CRM />} />
                  <Route path="mailforge/templates" element={<CRMTemplates />} />
                  <Route path="mailforge/mailbox" element={<CRMMailbox />} />
                  <Route path="mailforge/automations" element={<CRMAutomations />} />
                </Route>

                {/* Special Super Admin Viewer Route - Accessible by any admin */}
                <Route
                  path="/:companySlug/:username/super-admin-viewer"
                  element={<ProtectedRoute element={<ClientLayoutNew />} requiredRole="client" />}
                >
                  <Route index element={<SuperAdminViewer />} />
                  <Route path="dashboard" element={<SuperAdminViewer />} />
                  <Route path="assigned-tasks" element={<AssignedTasks />} />
                  <Route path="user-status" element={<UserStatus />} />
                </Route>

                {/* Generic Client Routes - Show 404 for invalid client routes */}
                <Route path="/client/login" element={<Static404 />} />
                <Route path="/client/dashboard" element={<Static404 />} />
                <Route path="/client/*" element={<Static404 />} />
                <Route path="/client" element={<Static404 />} />
                
                {/* Legacy Client Routes - Redirect to signup for security */}
                {/* REMOVED: This route was too broad and interfering with logout process */}
                {/* <Route
                  path="/client/:username"
                  element={<Navigate to="/client-signup" replace />}
                /> */}

                {/* Static 404 Route */}
                <Route path="/static-404" element={<Static404 />} />
                
                {/* Default Routes */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="*" element={<Smart404Router />} />
                              </Routes>
              </SnackbarProvider>
            </HeartbeatProvider>
                  </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;